import * as Sentry from '@sentry/react';
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { ApolloError } from '@apollo/client';
import { xTenantId } from '../../consts';
import { useUserProfileLazyQuery } from '../../libs/graphql/baseAppAPI/queries/__generated__/user-profile.query.generated';
import { UserCompliance, UserConsents, UserProfileResponse, LastLogins, UserKpis } from '../../hooks/registration';
import { BRANDS } from '../../components/Itv/types';
import { getXPToken } from '../../components/Layout/Promotions/services';
import { getStarBalance } from '../../components/Itv/ItvShop/service';
import { getActualXPToken } from '../../utils/auth';

interface Props {
  children: React.ReactNode;
}

export type StarBalance = {
  string: string;
  number: number;
};

interface ContextValue {
  fetching: boolean;
  error: ApolloError | undefined;
  user: UserProfileResponse | undefined | null;
  consents: UserConsents | undefined | null;
  lastLogins: LastLogins | undefined | null;
  compliance: UserCompliance | undefined | null;
  kpIs: UserKpis | undefined | null;
  userProfileId: string;
  fetchUserProfile: () => Promise<void>;
  theme: BRANDS;
  setTheme: (arg: BRANDS) => void;
  XPToken: string;
  setXPToken: (arg: string) => void;
  starBalance: StarBalance;
  setStarBalance: (arg: StarBalance) => void;
  updateStarBalance: boolean;
  setUpdateStarBalance: (arg: boolean) => void;
}

const initialValue: ContextValue = {
  fetching: false,
  user: undefined,
  error: undefined,
  compliance: undefined,
  consents: undefined,
  lastLogins: undefined,
  kpIs: undefined,
  userProfileId: '',
  fetchUserProfile: async () => undefined,
  theme: BRANDS.BINGO,
  setTheme: () => {},
  XPToken: '',
  setXPToken: () => {},
  starBalance: { string: '', number: 0 },
  setStarBalance: () => {},
  updateStarBalance: false,
  setUpdateStarBalance: () => {},
};

const UserContext = React.createContext(initialValue);

export const useUserContext = (): ContextValue => useContext(UserContext);

export const UserProvider: FC<Props> = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth?.user?.access_token || '';

  const [getUserProfile, { data, loading, error }] = useUserProfileLazyQuery({ fetchPolicy: 'network-only' });

  const [theme, setTheme] = useState<BRANDS>(initialValue.theme);
  const [XPToken, setXPToken] = useState<string>(initialValue.XPToken);
  const [starBalance, setStarBalance] = useState<StarBalance>({
    string: initialValue.starBalance.string,
    number: initialValue.starBalance.number,
  });
  const [updateStarBalance, setUpdateStarBalance] = useState<boolean>(initialValue.updateStarBalance);

  const fetchUserProfile = useCallback(async (): Promise<void> => {
    await getUserProfile({
      variables: {
        userProfileId: (auth.user?.profile?.user_profile_id as string) || '',
        xTenantId,
      },
    });
  }, [auth.user?.profile?.user_profile_id, getUserProfile]);

  const fetchXpToken = async (): Promise<void> => {
    const XPToken = await getXPToken();

    XPToken && setXPToken(XPToken);
  };

  useEffect(() => {
    if (auth?.isAuthenticated && accessToken && !data?.userProfile.user) {
      fetchXpToken();
      fetchUserProfile();
    }
  }, [auth?.isAuthenticated, accessToken, data?.userProfile.user]);

  useEffect(() => {
    if (XPToken) {
      (async (): Promise<void> => {
        try {
          const activeToken = await getActualXPToken(XPToken, setXPToken);
          const starBalance = await getStarBalance(activeToken);

          const numberStarBalance = Math.floor(Number(starBalance.tokens));
          const stringStarBalance = new Intl.NumberFormat('en-US').format(numberStarBalance);

          starBalance && setStarBalance({ string: stringStarBalance, number: numberStarBalance });
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [XPToken, updateStarBalance]);

  useEffect(() => {
    const profileId = auth.user?.profile?.user_profile_id as string | undefined;

    if (auth?.isAuthenticated && profileId) {
      Sentry.setUser({ id: profileId });
    } else {
      Sentry.setUser(null);
    }
  }, [auth?.isAuthenticated, auth.user?.profile?.user_profile_id]);

  const value: ContextValue = useMemo(() => {
    return {
      fetching: loading,
      user: data?.userProfile.user,
      consents: data?.userProfile.consents,
      lastLogins: data?.userProfile.lastLogins,
      compliance: data?.userProfile.compliance,
      kpIs: data?.userProfile.kpIs,
      userProfileId: (auth.user?.profile?.user_profile_id as string) || '',
      error,
      fetchUserProfile,
      theme,
      setTheme,
      XPToken,
      setXPToken,
      starBalance,
      setStarBalance,
      updateStarBalance,
      setUpdateStarBalance,
    };
  }, [
    data,
    loading,
    auth.user?.profile?.user_profile_id,
    error,
    fetchUserProfile,
    theme,
    XPToken,
    starBalance,
    updateStarBalance,
  ]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
